import React, { useState, useEffect } from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import { FaEdit } from 'react-icons/fa';
import RolesSelectionModal from './RolesSelectionModal';
import { organizationApi } from '../../services/api';
import { ROLE_TYPES } from '../../constants/roleTypes';

const RolesModalSelector = ({ 
  value = '', 
  onChange, 
  readOnly = false,
  getInitialPosition,
  item,
  ...props 
}) => {
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [displayRoles, setDisplayRoles] = useState([]);
  const [unmatchedRoles, setUnmatchedRoles] = useState([]);
  const [initialPosition, setInitialPosition] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    // Get the initial position from the employee data
    
    if (getInitialPosition && item) {
      const position = getInitialPosition(item);
      setInitialPosition(position);
      setSelectedPositionForRoles(position);
    }
  }, [getInitialPosition, item]);

  useEffect(() => {
    // Parse the comma-separated value into role names
    if (value && allRoles.length > 0) {
      const roleNames = value.split(',').map(r => r.trim()).filter(Boolean);
      const matchedRoles = allRoles.filter(role => roleNames.includes(role.label));
      const unmatchedRoleNames = roleNames.filter(roleName => 
        !allRoles.some(role => role.label === roleName)
      );
      
      setDisplayRoles(matchedRoles);
      setUnmatchedRoles(unmatchedRoleNames);
    } else {
      setDisplayRoles([]);
      setUnmatchedRoles([]);
    }
  }, [value, allRoles]);

  const fetchRoles = async () => {
    try {
      const rolesResponse = await organizationApi.getAllRoles();
      setAllRoles(rolesResponse);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setAllRoles([]);
    }
  };

  const handleEditClick = () => {
    // Set the position for the modal
    if (initialPosition) {
      setSelectedPositionForRoles(initialPosition);
    }
    
    // Parse current value and set up modal state
    if (value) {
      const roleNames = value.split(',').map(r => r.trim()).filter(Boolean);
      const matchedRoles = allRoles.filter(role => roleNames.includes(role.label));
      
      const functionalRoleIds = matchedRoles
        .filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
        .map(role => role.id);
      
      const technicalRoleIds = matchedRoles
        .filter(role => role.type === ROLE_TYPES.TECHNICAL)
        .map(role => role.id);
      
      const organizationalRoleIds = matchedRoles
        .filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
        .map(role => role.id);

      setSelectedFunctionalRoles(functionalRoleIds);
      setSelectedTechnicalRoles(technicalRoleIds);
      setSelectedOrganizationalRoles(organizationalRoleIds);
    } else {
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      setSelectedOrganizationalRoles([]);
    }
    
    setIsModalOpen(true);
  };

  const handleDeleteUnmatchedRole = (roleToDelete) => {
    // Remove the unmatched role from the value string
    const currentRoles = value.split(',').map(r => r.trim()).filter(Boolean);
    const updatedRoles = currentRoles.filter(role => role !== roleToDelete);
    const newValue = updatedRoles.join(', ');
    onChange(newValue);
  };

  const handleModalSubmit = () => {
    // Combine selected role IDs and convert to labels, plus any remaining unmatched roles
    const allSelectedRoleIds = [...selectedFunctionalRoles, ...selectedTechnicalRoles, ...selectedOrganizationalRoles];
    const selectedRoles = allRoles.filter(role => allSelectedRoleIds.includes(role.id));
    const matchedRoleLabels = selectedRoles.map(role => role.label);
    
    // Combine matched roles from modal with existing unmatched roles
    const combinedRoles = [...matchedRoleLabels, ...unmatchedRoles].filter(Boolean);
    const roleLabelsString = combinedRoles.join(', ');
    
    onChange(roleLabelsString);
    setIsModalOpen(false);
    
    // Reset modal state after saving
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
  };

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        p: 1,
        minHeight: '40px',
        width: '100%'
      }}>
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 0.5,
          alignItems: 'center'
        }}>
          {(displayRoles.length > 0 || unmatchedRoles.length > 0) ? (
            <>
              {/* Matched roles - colored chips */}
              {displayRoles.map((role, index) => (
                <Chip
                  key={`matched-${index}`}
                  label={role.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: role.type === ROLE_TYPES.FUNCTIONAL ? '#3B82F6' : '#10B981',
                    color: role.type === ROLE_TYPES.FUNCTIONAL ? '#3B82F6' : '#10B981',
                    fontSize: '12px'
                  }}
                />
              ))}
              
              {/* Unmatched roles - red deletable chips */}
              {unmatchedRoles.map((roleName, index) => (
                <Chip
                  key={`unmatched-${index}`}
                  label={roleName}
                  size="small"
                  variant="outlined"
                  onDelete={!readOnly ? () => handleDeleteUnmatchedRole(roleName) : undefined}
                  sx={{
                    borderColor: '#EF4444',
                    color: '#EF4444',
                    fontSize: '12px',
                    '& .MuiChip-deleteIcon': {
                      color: '#EF4444',
                      fontSize: '16px',
                      '&:hover': {
                        color: '#DC2626'
                      }
                    }
                  }}
                />
              ))}
            </>
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>
              {readOnly ? 'No roles assigned' : 'Click to select roles'}
            </span>
          )}
        </Box>
        {!readOnly && (
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{ 
              color: '#6B7280',
              '&:hover': {
                backgroundColor: '#F3F4F6'
              }
            }}
          >
            <FaEdit />
          </IconButton>
        )}
      </Box>

      <RolesSelectionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        selectedPositionForRoles={selectedPositionForRoles}
        setSelectedPositionForRoles={setSelectedPositionForRoles}
        initialPosition={initialPosition}
        selectedFunctionalRoles={selectedFunctionalRoles}
        setSelectedFunctionalRoles={setSelectedFunctionalRoles}
        selectedTechnicalRoles={selectedTechnicalRoles}
        setSelectedTechnicalRoles={setSelectedTechnicalRoles}
        selectedOrganizationalRoles={selectedOrganizationalRoles}
        setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
      />
    </>
  );
};

export default RolesModalSelector;